import React, { FC, memo, useCallback } from 'react'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import { getParentDepartment } from '@moonpig/web-core-utils'
import {
  trackUIEvent,
  createProductViewedEvent,
} from '@moonpig/web-core-analytics'
import { Box, Flex, Text } from '@moonpig/launchpad-components'
import {
  ProductClickedEvent,
  AddedToBasketEvent,
  Status,
  PromoTileData,
  calculateRowNumber,
  useNumberOfTilesPerRow,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { getDeviceType } from '@moonpig/web-shared-utils'
import { SHOW_RUDE_TOGGLE } from '@moonpig/web-core-brand/config'
import { useStoreId } from '@moonpig/web-core-stores'
import { ProductGridWithPaging } from '../ProductGridWithPaging'
import { useFindLocaleText } from '../../text-localisation'
import { SearchFiltersFacet } from '../../pages/GalleryPage/types'
import { useSetUIHCookie } from '../../utils'
import { useSearchStore } from '../../store/SearchStore'
import { RudeToggle } from '../FiltersMenu'
import { InvertedFilterToggle } from '../../services/types/services'
import { SearchContext } from '../../store/slices/shared'

type ProductGridWithTrackingProps = {
  totalCount: number
  pageTitle: string
  metaTitle: string
  searchContext: SearchContext
  products: ProductTileProduct[]
  presetFacets: SearchFiltersFacet[]
  experiments?: { [key: string]: string } | undefined
  isInternalUser?: boolean
  onAddedToBasket: (e: AddedToBasketEvent) => void
  pageType: PageTypeEnum
  customerId?: string
  groupCardProject?: string
  promoTile?: PromoTileData
}

export const ProductGridWithTrackingComponent: FC<
  ProductGridWithTrackingProps
> = ({
  totalCount,
  metaTitle,
  searchContext,
  products,
  experiments,
  onAddedToBasket,
  isInternalUser = false,
  pageType,
  customerId,
  groupCardProject,
  promoTile,
}) => {
  const storeId = useStoreId()
  const { searchTerm, departments } = searchContext
  const localiseText = useFindLocaleText()
  const { setUIHCookie } = useSetUIHCookie()
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const filters = useSearchStore(store => store.filters)
  const preAppliedFilters = useSearchStore(store => store.preAppliedFilters)
  const productsLoading = useSearchStore(store => store.loading)

  const onProductClicked = useCallback(
    async ({ product }: ProductClickedEvent) => {
      setUIHCookie({
        product,
        facets: [
          ...selectedFilters.map(f => ({ facetKey: f.id, group: f.parent })),
          ...preAppliedFilters.map(f => ({
            facetKey: f.key,
            group: f.group,
          })),
        ],
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setUIHCookie, selectedFilters, preAppliedFilters],
  )

  const parentDepartment = getParentDepartment(departments[0])

  const isCards = parentDepartment === 'ALL_CARDS'

  const currentTilesPerRow = useNumberOfTilesPerRow()

  const onProductFirstClick = useCallback(
    async ({ product, index }: ProductClickedEvent) => {
      const { mnpg_ui_events_api_correlation_id: correlationId } =
        getBrowserCookies()
      if (process.env.API_URL) {
        try {
          await trackUIEvent(
            createProductViewedEvent({
              productId: product.id,
              searchTerm,
              department: departments,
              correlationId,
              filters: [
                ...selectedFilters.map(x => ({
                  facetKey: x.id,
                })),
                ...preAppliedFilters.map(x => ({
                  facetKey: x.key,
                })),
              ],
              store: storeId.toUpperCase(),
              ...(experiments && { metaData: { experiments } }),
              source: getDeviceType(),
              customerId,
              rowNumber: calculateRowNumber(
                (index ?? 0) + 1,
                currentTilesPerRow,
              ),
            }),
          )
        } catch (error) {
          // @eslint-disable-next-line @typescript-eslint/no-empty
        }
      }
    },
    [
      searchTerm,
      departments,
      selectedFilters,
      preAppliedFilters,
      storeId,
      experiments,
      customerId,
      currentTilesPerRow,
    ],
  )

  const onAddedToBasketWithTracking = React.useCallback(
    (e: AddedToBasketEvent) => {
      onAddedToBasket({
        ...e,
        componentName: 'product grid',
      })
    },
    [onAddedToBasket],
  )

  const notRudeFilterFromStore = filters.find(
    f => f.__typename === 'FilterInvertedToggle' && f.id === 'not-rude',
  ) as InvertedFilterToggle

  const enableRudeToggle =
    SHOW_RUDE_TOGGLE && isCards && !!notRudeFilterFromStore

  return (
    <Box>
      <Status
        loading={{
          status: productsLoading,
          fixed: false,
          zIndex: 999,
        }}
      />
      <Flex
        justifyContent="space-between"
        pr={{ xs: 6, xl: 0 }}
        pl={{ xs: 7, xl: 4 }}
      >
        {totalCount > 0 && (
          <Text
            data-testid="web-find-total-product-count"
            color="colorTextHeadline"
            typography={'typeBodyText'}
            paddingY={6}
          >
            {totalCount} {localiseText('find.results')}
          </Text>
        )}

        {enableRudeToggle && <RudeToggle filter={notRudeFilterFromStore} />}
      </Flex>
      <ProductGridWithPaging
        totalCount={totalCount}
        region={storeId}
        products={products}
        onProductClicked={onProductClicked}
        onProductFirstClick={onProductFirstClick}
        onAddToBasket={onAddedToBasketWithTracking}
        isInternalUser={isInternalUser}
        metaTitle={metaTitle}
        pageType={pageType}
        groupCardProject={groupCardProject}
        promoTile={promoTile}
      />
    </Box>
  )
}

export const ProductGridWithTracking = memo(ProductGridWithTrackingComponent)
